import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import { formatAwb } from '../../utils/awb';
import {
  Box,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Print as PrintIcon,
  QrCode2 as QrCodeIcon,
  Inventory as InventoryIcon,
  FlightTakeoff as FlightIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Timeline as TrackingIcon,
} from '@mui/icons-material';

const WarehouseDetail = () => {
  const { whId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (whId) {
      fetchWarehouseDetail();
    }
  }, [whId]);

  const fetchWarehouseDetail = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Determinar si es ID numérico o referencia
      const isNumeric = /^\d+$/.test(whId);
      const params = isNumeric 
        ? `id=${whId}` 
        : `reference=${whId.startsWith('WH-') ? whId : `WH-${whId}`}`;
      
      const res = await api.get(`${endpoints.warehouse.detail}?${params}`);
      
      if (res.data.success && res.data.data) {
        setData(res.data.data);
      } else {
        setError('Warehouse no encontrado');
      }
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError(err.response?.data?.message || 'Error al cargar el warehouse');
    } finally {
      setLoading(false);
    }
  };

  // Generar URL del QR
  const qrCodeUrl = useMemo(() => {
    if (!data) return '';
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const detailUrl = `${baseUrl}/warehouse/detail/${data.id}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(detailUrl)}`;
  }, [data]);

  // Obtener color de status
  const getStatusColor = (status) => {
    switch (status) {
      case 'received': return 'success';
      case 'partial': return 'warning';
      case 'staged': return 'info';
      case 'loaded': return 'primary';
      case 'flown': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'received': return 'Recibido';
      case 'partial': return 'Parcial';
      case 'staged': return 'Asignado';
      case 'loaded': return 'Cargado';
      case 'flown': return 'Volado';
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'received': return <InventoryIcon />;
      case 'partial': return <WarningIcon />;
      case 'staged': return <PendingIcon />;
      case 'loaded': return <ShippingIcon />;
      case 'flown': return <FlightIcon />;
      default: return <InventoryIcon />;
    }
  };

  // Imprimir
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography color="text.secondary">Cargando información del Warehouse...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<BackIcon />}
          onClick={() => navigate('/warehouse')}
        >
          Volver al Warehouse
        </Button>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="warning">
          No se encontró información para WH #{whId}
        </Alert>
        <Button
          variant="contained"
          startIcon={<BackIcon />}
          onClick={() => navigate('/warehouse')}
          sx={{ mt: 2 }}
        >
          Volver al Warehouse
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }} className="warehouse-detail">
      {/* Header */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #37474F 0%, #455A64 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <InventoryIcon sx={{ fontSize: 48 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {data.reference || `WH-${data.id}`}
                </Typography>
                <Chip
                  icon={getStatusIcon(data.status)}
                  label={getStatusLabel(data.status)}
                  color={getStatusColor(data.status)}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>
            {data.awb && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  AWB: {formatAwb ? formatAwb(data.awb) : data.awb}
                </Typography>
                <Tooltip title="Ver Tracking">
                  <IconButton 
                    size="small" 
                    component={Link}
                    to={`/warehouse/tracking?awb=${data.awb}`}
                    sx={{ color: 'white' }}
                  >
                    <TrackingIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            {qrCodeUrl && (
              <Box sx={{ display: 'inline-block', bgcolor: 'white', p: 1, borderRadius: 1 }}>
                <img src={qrCodeUrl} alt="QR Code" style={{ width: 120, height: 120 }} />
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Acciones */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }} className="no-print">
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          component={Link}
          to="/warehouse/inventory"
        >
          Volver al Inventario
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Imprimir
        </Button>
      </Box>

      {/* Información Principal */}
      <Grid container spacing={3}>
        {/* Datos Generales */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InventoryIcon color="primary" />
                Información General
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: 140 }}>Referencia:</TableCell>
                      <TableCell>{data.reference}</TableCell>
                    </TableRow>
                    {data.awb && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>AWB:</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {formatAwb ? formatAwb(data.awb) : data.awb}
                            <Tooltip title="Ver Tracking">
                              <IconButton 
                                size="small" 
                                component={Link}
                                to={`/warehouse/tracking?awb=${data.awb}`}
                                color="primary"
                              >
                                <TrackingIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Destino:</TableCell>
                      <TableCell>
                        <Chip label={data.destination} color="primary" size="small" />
                      </TableCell>
                    </TableRow>
                    {data.origin && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Origen:</TableCell>
                        <TableCell>{data.origin}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Estado:</TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(data.status)} 
                          color={getStatusColor(data.status)} 
                          size="small"
                          icon={getStatusIcon(data.status)}
                        />
                      </TableCell>
                    </TableRow>
                    {data.priority && data.priority !== 'normal' && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Prioridad:</TableCell>
                        <TableCell>
                          <Chip 
                            label={data.priority.toUpperCase()} 
                            color={data.priority === 'vip' ? 'secondary' : 'warning'} 
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    {data.nature && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Naturaleza:</TableCell>
                        <TableCell>{data.nature}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Cantidades */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShippingIcon color="primary" />
                Cantidades
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                    <Typography variant="caption" color="text.secondary">Piezas Recibidas</Typography>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {data.pieces_received || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                    <Typography variant="caption" color="text.secondary">Piezas Disponibles</Typography>
                    <Typography variant="h4" fontWeight="bold" color="info.main">
                      {data.pieces_remaining ?? data.pieces_received ?? 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                    <Typography variant="caption" color="text.secondary">Peso Recibido (kg)</Typography>
                    <Typography variant="h4" fontWeight="bold" color="warning.main">
                      {parseFloat(data.weight_received || 0).toLocaleString()}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.50' }}>
                    <Typography variant="caption" color="text.secondary">Peso Disponible (kg)</Typography>
                    <Typography variant="h4" fontWeight="bold" color="secondary.main">
                      {parseFloat(data.weight_remaining ?? data.weight_received ?? 0).toLocaleString()}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Volumen si existe */}
              {data.volume > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
                    <Typography variant="caption" color="text.secondary">Volumen (lb)</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {parseFloat(data.volume || 0).toLocaleString()}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* DG Info */}
        {data.is_dangerous_goods === 1 && (
          <Grid item xs={12}>
            <Alert severity="warning" icon={<WarningIcon />}>
              <Typography fontWeight="bold">Mercancía Peligrosa (DG)</Typography>
              <Typography variant="body2">
                Clase: {data.dg_class || '-'} | UN: {data.dg_un || '-'}
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Fechas y Tracking */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PendingIcon color="primary" />
                Tracking
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {data.received_at && (
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'success.main' }}>
                      <Typography variant="caption" color="text.secondary">Recibido</Typography>
                      <Typography fontWeight="bold">
                        {new Date(data.received_at).toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {data.reserved_flight_awb && (
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'info.main' }}>
                      <Typography variant="caption" color="text.secondary">Vuelo Reservado</Typography>
                      <Typography fontWeight="bold">
                        {data.reserved_flight_awb}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {data.shipper_nombre && (
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'primary.main' }}>
                      <Typography variant="caption" color="text.secondary">Shipper</Typography>
                      <Typography fontWeight="bold">
                        {data.shipper_nombre}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {data.consignee_nombre && (
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'secondary.main' }}>
                      <Typography variant="caption" color="text.secondary">Consignatario</Typography>
                      <Typography fontWeight="bold">
                        {data.consignee_nombre}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {data.agente_nombre && (
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'warning.main' }}>
                      <Typography variant="caption" color="text.secondary">Agente</Typography>
                      <Typography fontWeight="bold">
                        {data.agente_nombre}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              {/* Notas */}
              {data.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notas:
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography>{data.notes}</Typography>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            .warehouse-detail { padding: 0 !important; }
          }
        `}
      </style>
    </Box>
  );
};

export default WarehouseDetail;

